// User Data Service - Centralized user data management and retrieval
import api, { authUtils } from './api.js'
import { ROUTES } from '../config/routes.js'
import { USER_TYPES } from '../constants/userTypes'
import { clearAuthData } from '../utils/sessionUtils.js'

// NOTE: Route permissions are now handled centrally in src/config/routes.js
// The ROUTE_PERMISSIONS object has been removed to eliminate duplication.

// Default redirects for each user type
export const DEFAULT_REDIRECTS = {
  [USER_TYPES.PET_GUARDIAN]: '/dashboard/pets',
  [USER_TYPES.VETERINARIAN]: '/medical/clinic/redirect-to-clinic',
  [USER_TYPES.SERVICE_PROVIDER]: '/medical/dashboard/petshopowner',
  [USER_TYPES.CLINIC_MODERATOR]: '/medical/clinic/redirect-to-clinic',
  [USER_TYPES.FIRM]: '/social', // Firm users land on Connect (Sulhafa Connect)
  [USER_TYPES.ADMIN]: '/admin/dashboard',
  [USER_TYPES.CONTENT_ADMIN]: '/admin/dashboard/cms/banners',
  [USER_TYPES.DEV_ADMIN]: '/dev-admin'
}

class UserDataService {
  constructor() {
    this.userData = null
    this.isLoading = false
    this.error = null
  }

  // Get current user data from localStorage or API
  async getCurrentUserData() {
    try {
      this.isLoading = true
      this.error = null

      // First try to get from localStorage
      const localUserData = authUtils.getCurrentUser()

      if (localUserData && this.isValidUserData(localUserData)) {
        this.userData = localUserData
        return localUserData
      }

      // If no valid local data, try to fetch from API
      const token = localStorage.getItem('access_token')
      if (!token) {
        clearAuthData()
        window.location.href = '/auth/login'
        // throw new Error('No authentication token found')
      }

      // Fetch fresh user data from API
      const freshUserData = await this.fetchUserDataFromAPI(token)

      if (freshUserData) {
        this.userData = freshUserData
        // Update localStorage with fresh data
        authUtils.setUserData(freshUserData, token)
        return freshUserData
      }

      throw new Error('Failed to retrieve user data')
    } catch (error) {
      console.error('Error getting user data:', error)
      this.error = error.message
      return null
    } finally {
      this.isLoading = false
    }
  }

  // Fetch user data from API — through the axios instance so a 401 here
  // goes through the token-refresh flow instead of hard-logging out (this
  // runs on every auth route remount, e.g. browser Back to the feed).
  async fetchUserDataFromAPI(token) {
    try {
      const { data } = await api.get('/api/v1/user-profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      // Handle different response formats
      if (data?.status === 'success' && data.data) {
        return data.data
      } else if (data?.user || data?.id) {
        return data
      } else {
        throw new Error('Invalid user data format received')
      }
    } catch (error) {
      console.error('Error fetching user data from API:', error)
      throw error
    }
  }

  // Validate user data structure
  isValidUserData(userData) {
    if (!userData || typeof userData !== 'object') {
      return false
    }

    // Check for required fields
    const hasId = !!(userData.id || userData.user_id)
    const hasUserType = !!(userData.user_type || userData.userType)

    return hasId && hasUserType
  }

  // Get user type
  getUserType() {

    if (!this.userData) {
      return null; // Default fallback
    }

    const userType = this.userData.user_type || this.userData.userType || this.userData.type

    // Normalize user type
    const typeMapping = {
      'PET_GUARDIAN': USER_TYPES.PET_GUARDIAN,
      'VETERINARIAN': USER_TYPES.VETERINARIAN,
      'SERVICE_PROVIDER': USER_TYPES.SERVICE_PROVIDER,
      'PET_SHOP_OWNER': USER_TYPES.SERVICE_PROVIDER, // Backend uses SERVICE_PROVIDER (legacy: PET_SHOP_OWNER maps to SERVICE_PROVIDER)
      'pet_guardian': USER_TYPES.PET_GUARDIAN,
      'veterinarian': USER_TYPES.VETERINARIAN,
      'service_provider': USER_TYPES.SERVICE_PROVIDER, // Backend uses SERVICE_PROVIDER
      'pet_shop_owner': USER_TYPES.SERVICE_PROVIDER, // Backend uses SERVICE_PROVIDER (legacy: pet_shop_owner maps to SERVICE_PROVIDER)
      'CLINIC_MODERATOR': USER_TYPES.CLINIC_MODERATOR,
      'FIRM': USER_TYPES.FIRM,
      'firm': USER_TYPES.FIRM,
      'ADMIN': USER_TYPES.ADMIN,
      'admin': USER_TYPES.ADMIN,
      'admin': USER_TYPES.ADMIN,
      'DEV_ADMIN': USER_TYPES.DEV_ADMIN,
      'dev_admin': USER_TYPES.DEV_ADMIN,
      'CONTENT_ADMIN': USER_TYPES.CONTENT_ADMIN,
      'content_admin': USER_TYPES.CONTENT_ADMIN
    }

    return typeMapping[userType] || userType
  }

  // Check if user has permission for a specific route
  hasRoutePermission(routePath) {
    const userType = this.getUserType()

    // Find matching route in centralized routes
    const routeEntry = Object.entries(ROUTES).find(([path, config]) => {
      // Check exact match first
      if (path === routePath) return true

      // Check pattern matches (for dynamic routes like /pets/:petId)
      // 1. Handle optional segments like /:id? -> turn into optional group (?:/([^/]+))?
      // 2. Handle required segments like :id -> turn into [^/]+
      // 3. Splat like /* -> match zero or more trailing segments (last: the
      //    replacement text above contains '?' and ':' which the earlier
      //    passes would otherwise re-consume)
      const pattern = path
        .replace(/\/:[^/]+\?/g, '(?:/([^/]+))?')
        .replace(/:[^/]+/g, '[^/]+')
        .replace(/\/\*$/, '(?:/.*)?')

      const regex = new RegExp(`^${pattern}$`)
      return regex.test(routePath)
    })

    if (!routeEntry) return false

    const [_, config] = routeEntry

    // Check if route requires authentication
    if (!config.middleware.requireAuth) return true

    // Check if user type is allowed
    return config.middleware.allowedUserTypes.includes(userType)
  }

  // Get default redirect path for user type
  getDefaultRedirect() {
    const userType = this.getUserType()
    return DEFAULT_REDIRECTS[userType] || '/'
  }

  // Check if user is authenticated
  isAuthenticated() {
    return authUtils.isAuthenticated() && this.userData !== null
  }

  // Get user display name
  getDisplayName() {
    if (!this.userData) return 'User'

    if (this.userData.first_name && this.userData.last_name) {
      return `${this.userData.first_name} ${this.userData.last_name}`
    }

    if (this.userData.first_name) {
      return this.userData.first_name
    }

    return this.userData.display_name ||
      this.userData.username ||
      this.userData.name ||
      this.userData.email?.split('@')[0] ||
      'User'
  }

  // Get user profile image URL
  getProfileImageUrl() {
    if (!this.userData) return null

    if (this.userData.email === 'test@example.com') {
      return "/icons/person.png"
    }

    const imageUrl = this.userData.profile_image_url ||
      this.userData.profileImageUrl ||
      this.userData.avatar ||
      this.userData.profile_image

    if (imageUrl) {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      if (imageUrl.startsWith('/storage/') || imageUrl.startsWith('/uploads/')) {
        return `${API_BASE_URL}${imageUrl}`
      }
      if (imageUrl.startsWith('http')) return imageUrl
      return `${API_BASE_URL}/${imageUrl}`
    }

    return null
  }

  // Clear user data
  clearUserData() {
    this.userData = null
    this.error = null
    this.isLoading = false
    authUtils.clearUserData()
  }

  // Update user data
  updateUserData(updates) {
    if (this.userData && updates) {
      this.userData = { ...this.userData, ...updates }
      authUtils.setUserData(this.userData)
    }
  }
}

// Create singleton instance
const userDataService = new UserDataService()

export default userDataService
